import { ActionIcon, Box, CloseButton, FileButton, Image, type MantineSize } from '@mantine/core';
import { IconPhotoSearch } from '@tabler/icons-react';
import { useState } from 'react';
import styles from './imageInput.module.css';

interface ImageInputProps {
    size?: MantineSize;
    imageUrl?: string;
    label?: string;
    disabled?: boolean;
    error?: React.ReactNode;
    onChange?: (file: File | null) => void;
}

export const ImageInput = ({ imageUrl, onChange, disabled }: ImageInputProps) => {
    const [activeImageUrl, setActiveImageUrl] = useState<string | undefined>(imageUrl);

    // Sync internal state when the imageUrl prop changes (during render, per React docs)
    const [prevImageUrl, setPrevImageUrl] = useState(imageUrl);
    if (imageUrl !== prevImageUrl) {
        setPrevImageUrl(imageUrl);
        setActiveImageUrl(imageUrl);
    }

    const handleSetImage = (file: File | null) => {
        if (file) setActiveImageUrl(URL.createObjectURL(file));
        else setActiveImageUrl(undefined);
        onChange?.(file);
    };

    return (
        <Box
            p={10}
            flex={3}
            bg={'light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))'}
            bdrs={4}
            bd={'1px solid light-dark(var(--mantine-color-gray-4), var(--mantine-color-dark-4))'}
        >
            {activeImageUrl && (
                <Box pos="relative" w="fit-content" maw="100%">
                    <Image
                        h={40}
                        w="auto"
                        maw="100%"
                        fit="contain"
                        src={activeImageUrl}
                        onLoad={() => URL.revokeObjectURL(activeImageUrl)}
                    />
                    {!disabled && (
                        <CloseButton
                            size="xs"
                            radius="xl"
                            variant="transparent"
                            className={styles.clearButton}
                            pos="absolute"
                            top={-8}
                            right={-8}
                            aria-label="Remove image"
                            onClick={() => handleSetImage(null)}
                        />
                    )}
                </Box>
            )}
            {!activeImageUrl && (
                <FileButton onChange={handleSetImage} accept="image/png,image/jpeg" disabled={disabled}>
                    {(props) => (
                        <ActionIcon variant="subtle" {...props} size={30}>
                            <IconPhotoSearch size="100%" stroke={1.5} />
                        </ActionIcon>
                    )}
                </FileButton>
            )}
        </Box>
    );
};
